import {
  Badge,
  Box,
  Button,
  Checkbox,
  Input,
  Modal,
  Popover,
  ScrollArea,
  TextInput,
  TextInputProps,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronsDown,
  IconChevronsUp,
  IconSearch,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { DepartmentListResponse } from 'api/actions/department-list/department-list-response';
import { UserGetFollowedDepartmentsListResponse } from 'api/actions/user-get-followed-departments-list/user-get-followed-departments-list-response';
import { formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';
import DepartmentParentReference from 'components/department/DepartmentParentReference';
import {
  DataMultiSelect,
  DataMultiSelectInheritedProps,
  DataSelect,
  DataSelectInheritedProps,
} from 'components/selects/DataSelect';
import { isNumber } from 'lodash';
import panic from 'errors/panic';
import { useDepartmentText } from 'model/DepartmentTerminology';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import ActionIcon from 'components/ActionIcon';
import { nanoid } from 'nanoid';
import useLocalStorage from 'hooks/use-local-storage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import removeAccents from 'remove-accents';
import 'components/selects/DepartmentSelect.css';

const EMPTY_ORGANIZATION_IDS: string[] = [];
const EXPANDED_STORAGE_KEY = 'objekto.department-select.expanded';
const AUTO_CONFIRM_STORAGE_KEY = 'objekto.department-select.auto-confirm';

/** Reads the shared preference when opening another already-mounted selector. */
function readAutoConfirmPreference() {
  try {
    return JSON.parse(localStorage.getItem(AUTO_CONFIRM_STORAGE_KEY) ?? '{}')?.enabled === true;
  } catch {
    return false;
  }
}

/** Refreshes branch choices saved by another mounted selector. */
function readExpandedPreference(): Record<string, boolean> {
  try {
    const stored = JSON.parse(localStorage.getItem(EXPANDED_STORAGE_KEY) ?? '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.entries(stored as Record<string, unknown>).reduce<Record<string, boolean>>(
      (result, [id, expanded]) => {
        if (typeof expanded === 'boolean') result[id] = expanded;
        return result;
      },
      {}
    );
  } catch {
    return {};
  }
}

export type DepartmentSelectItem = Pick<
  DepartmentListResponse[number],
  'departmentId' | 'departmentName' | 'status' | 'organization' | 'breadcrumbs'
> & {
  group: string;
  label: string;
  value: string;
};

type DepartmentTreeNode = {
  id: number;
  name: string;
  levelLabel: string;
  item?: DepartmentSelectItem;
  children: DepartmentTreeNode[];
};

/** Builds actual nested branches, including context ancestors omitted by permission-scoped reads. */
function buildDepartmentTree(items: DepartmentSelectItem[]) {
  const groups = new Map<
    number,
    { name: string; roots: DepartmentTreeNode[]; nodes: Map<number, DepartmentTreeNode> }
  >();

  items.forEach((item) => {
    const organizationId = item.organization.organizationId;
    if (!groups.has(organizationId)) {
      groups.set(organizationId, { name: item.organization.organizationName ?? '', roots: [], nodes: new Map() });
    }
    const group = groups.get(organizationId)!;
    item.breadcrumbs.forEach((breadcrumb, index) => {
      let node = group.nodes.get(breadcrumb.departmentId);
      if (!node) {
        node = {
          id: breadcrumb.departmentId,
          name: breadcrumb.departmentName,
          levelLabel: breadcrumb.levelLabel,
          children: [],
        };
        group.nodes.set(node.id, node);
        const parent = index > 0 ? group.nodes.get(item.breadcrumbs[index - 1].departmentId) : undefined;
        if (parent) parent.children.push(node);
        else group.roots.push(node);
      }
      if (breadcrumb.departmentId === item.departmentId) node.item = item;
    });
  });

  /** Orders siblings by localized name and ID. */
  const compare = (left: DepartmentTreeNode, right: DepartmentTreeNode) =>
    left.name.localeCompare(right.name, 'sk') || left.id - right.id;
  /** Applies sibling ordering at every depth. */
  const sort = (nodes: DepartmentTreeNode[]) => {
    nodes.sort(compare);
    nodes.forEach((node) => sort(node.children));
  };
  return [...groups.entries()]
    .sort((left, right) => left[1].name.localeCompare(right[1].name, 'sk') || left[0] - right[0])
    .map(([id, group]) => {
      sort(group.roots);
      return { id, name: group.name, roots: group.roots };
    });
}

/** Keeps only matching nodes and their ancestors; a matching parent does not pull in its children. */
function filterDepartmentTree(nodes: DepartmentTreeNode[], query: string, matchFullPath = false): DepartmentTreeNode[] {
  if (!query) return nodes;
  return nodes.flatMap((node) => {
    const path = node.item ? formatDepartmentPath(node.item.breadcrumbs) : node.name;
    const directMatch = removeAccents(matchFullPath ? path : node.name)
      .toLocaleLowerCase('sk')
      .includes(query);
    const children =
      directMatch && matchFullPath ? node.children : filterDepartmentTree(node.children, query, matchFullPath);
    return directMatch || children.length ? [{ ...node, children }] : [];
  });
}

/** Highlights every occurrence of the search term in a node's own name. */
function highlightDepartmentName(name: string, query: string) {
  if (!query) return name;
  const normalized = removeAccents(name).toLocaleLowerCase('sk');
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let match = normalized.indexOf(query);
  while (match !== -1) {
    parts.push(name.slice(cursor, match));
    parts.push(<mark key={match}>{name.slice(match, match + query.length)}</mark>);
    cursor = match + query.length;
    match = normalized.indexOf(query, cursor);
  }
  parts.push(name.slice(cursor));
  return parts;
}

/**
 * Normalizes organization ids coming from either a single-value or multi-value source.
 */
export const normalizeOrganizationIds = ({
  organizationId,
  organizationIds,
}: {
  organizationId?: number;
  organizationIds?: Array<number | string>;
}): string[] => {
  if (Array.isArray(organizationIds) && organizationIds.length > 0) {
    return organizationIds.map(String).filter(Boolean);
  }

  if (isNumber(organizationId) && organizationId !== -1) {
    return [String(organizationId)];
  }

  return EMPTY_ORGANIZATION_IDS;
};

/**
 * The shared props of the select components.
 */
interface DepartmentSelectProp {
  showDiscarded?: boolean;
  groupByOrganization?: boolean;
  organizationId?: number;
  organizationIds?: Array<number | string>;
  permissionSlug?: string;
  excludeDepartmentId?: number;
  preloadedDepartments?: DepartmentListResponse;
  followedOnly?: boolean;
  userId?: number;
}

/**
 * Fetches departments form API.
 */
function useDepartmentSelect({
  showDiscarded,
  groupByOrganization,
  organizationId,
  organizationIds = EMPTY_ORGANIZATION_IDS,
  permissionSlug,
  excludeDepartmentId,
  preloadedDepartments,
  followedOnly = false,
  userId,
}: DepartmentSelectProp) {
  const { getAction } = useApi();
  const organizationIdsKey = useMemo(
    () => normalizeOrganizationIds({ organizationId, organizationIds }).join(','),
    [organizationId, Array.isArray(organizationIds) ? organizationIds.map(String).join(',') : '']
  );

  const normalizedOrganizationIds = useMemo(
    () => (organizationIdsKey ? organizationIdsKey.split(',').filter(Boolean) : EMPTY_ORGANIZATION_IDS),
    [organizationIdsKey]
  );

  const normalizedOrganizationIdsSet = useMemo(() => new Set(normalizedOrganizationIds), [organizationIdsKey]);

  const fetchDepartments = useCallback(async () => {
    const departments: Array<Omit<DepartmentSelectItem, 'label' | 'value' | 'group'>> = preloadedDepartments
      ? preloadedDepartments
      : followedOnly
      ? (await getAction('UserGetFollowedDepartmentsList')({ parameters: { userId: String(userId) } })).map(
          (department: UserGetFollowedDepartmentsListResponse[number]) => ({
            departmentId: department.departmentId,
            departmentName: department.departmentName,
            breadcrumbs: department.breadcrumbs,
            status: true,
            organization: { organizationId: department.organizationId, organizationName: department.organizationName },
          })
        )
      : await getAction('DepartmentList')({
          query: {
            filters: {
              'organizationId.eq':
                normalizedOrganizationIds.length === 1 ? Number(normalizedOrganizationIds[0]) : undefined,
              'showDiscarded.eq': showDiscarded ? 1 : 0,
              'permissionSlug.eq': permissionSlug,
            },
          },
        });

    const filteredDepartments =
      normalizedOrganizationIds.length > 0
        ? departments.filter(({ organization }) =>
            normalizedOrganizationIdsSet.has(String(organization.organizationId))
          )
        : departments;

    return [...filteredDepartments]
      .filter(
        ({ breadcrumbs }) =>
          !excludeDepartmentId || !breadcrumbs.some(({ departmentId }) => departmentId === excludeDepartmentId)
      )
      .sort(
        (left, right) =>
          (left.organization.organizationName ?? '').localeCompare(right.organization.organizationName ?? '', 'sk') ||
          formatDepartmentPath(left.breadcrumbs).localeCompare(formatDepartmentPath(right.breadcrumbs), 'sk') ||
          left.departmentId - right.departmentId
      )
      .map((department) => ({
        ...department,
        status: department.status ?? true,
        label: (department.status === false ? '[VYRADENÉ] ' : '') + formatDepartmentPath(department.breadcrumbs),
        value: String(department.departmentId),
        group: groupByOrganization ? department.organization.organizationName ?? '' : '',
      }));
  }, [
    getAction,
    showDiscarded,
    groupByOrganization,
    permissionSlug,
    normalizedOrganizationIds.length,
    normalizedOrganizationIdsSet,
    excludeDepartmentId,
    preloadedDepartments,
    followedOnly,
    userId,
  ]);

  return { fetchDepartments };
}

/**
 * The type of the Department Select component.
 */
type DepartmentSelectProps = DepartmentSelectProp &
  DataSelectInheritedProps & { multiple?: false; allowLegacy?: boolean };

/**
 * The type of the Department Multiselect component.
 */
type DepartmentMultiSelectOptions = DepartmentSelectProp &
  DataMultiSelectInheritedProps & {
    multiple: true;
    allowLegacy?: boolean;
    excludeSubtrees?: boolean;
    excludedIds?: Array<string | number>;
    showSelectionCount?: boolean;
    withinPortal?: boolean;
    loading?: boolean;
    loadError?: boolean;
    onRetry?: () => void;
    onPickerClose?: () => void;
    emptyText?: string;
    errorText?: string;
  };

type DepartmentMultiSelectProps = DepartmentMultiSelectOptions &
  (
    | { selectSubtrees?: false; pruneCoveredIds?: false; coveredByIds?: never }
    | { selectSubtrees: true; pruneCoveredIds?: boolean; coveredByIds?: Array<string | number> }
  ) &
  (
    | { inline: true; popover?: false; confirmSelection?: false }
    | { inline?: false; popover: true; confirmSelection?: false }
    | { inline?: false; popover?: false; confirmSelection?: true }
  );

type DepartmentSelectStrategyName = 'legacy' | 'tree';

/** Uses the viewer-wide session decision shared with all department surfaces. */
function useDepartmentSelectStrategy(): { name: DepartmentSelectStrategyName; pending: boolean } {
  const { mode, pending } = useDepartmentUiMode();
  return { name: mode, pending };
}

/** Historical pre-modal dropdown restored from the parent of commit 43aa7fa8. */
function LegacyDepartmentSelectStrategy({
  showDiscarded = true,
  groupByOrganization = false,
  organizationId,
  organizationIds = EMPTY_ORGANIZATION_IDS,
  permissionSlug,
  excludeDepartmentId,
  preloadedDepartments,
  followedOnly,
  userId,
  allowLegacy,
  ...props
}: DepartmentSelectProps) {
  const organizationIdsKey = useMemo(
    () => normalizeOrganizationIds({ organizationId, organizationIds }).join(','),
    [organizationId, Array.isArray(organizationIds) ? organizationIds.map(String).join(',') : '']
  );
  const key = useMemo(
    () => nanoid(),
    [showDiscarded, groupByOrganization, organizationId, organizationIdsKey, permissionSlug, excludeDepartmentId]
  );
  const { fetchDepartments } = useDepartmentSelect({
    showDiscarded,
    groupByOrganization,
    organizationId,
    organizationIds,
    permissionSlug,
    excludeDepartmentId,
    preloadedDepartments,
    followedOnly,
    userId,
  });
  const fetchFlatDepartments = useCallback(
    async () =>
      (await fetchDepartments()).map((department) => ({
        ...department,
        label: `${department.status ? '' : '[VYRADENÉ] '}${department.departmentName}`,
      })),
    [fetchDepartments]
  );

  return (
    <DataSelect
      key={key}
      action={fetchFlatDepartments}
      labelProp="label"
      valueProp="value"
      groupProp="group"
      {...props}
    />
  );
}

/** Historical flat dropdown used by revision and HR notification preferences. */
function LegacyDepartmentMultiSelect(props: DepartmentMultiSelectProps) {
  const { fetchDepartments } = useDepartmentSelect({ ...props, showDiscarded: props.showDiscarded ?? true });
  const fetchFlatDepartments = useCallback(
    async () =>
      (await fetchDepartments()).map((department) => ({
        ...department,
        label: `${department.status ? '' : '[VYRADENÉ] '}${department.departmentName}`,
      })),
    [fetchDepartments]
  );

  return (
    <DataMultiSelect
      action={fetchFlatDepartments}
      labelProp="label"
      valueProp="value"
      groupProp="group"
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      onReady={props.onReady}
      filterData={props.filterData}
      label={props.label}
      placeholder={props.placeholder}
      size={props.size}
      name={props.name}
      disabled={props.disabled}
      readOnly={props.readOnly}
      error={props.error}
      required={props.required}
      clearable={props.clearable}
      lazyLoad={props.lazyLoad}
      compact={props.compact}
    />
  );
}

interface DepartmentTreeBranchProps {
  nodes: DepartmentTreeNode[];
  depth: number;
  stickyTopOffset: number;
  stickyDepthLimit?: number;
  selectedValue: string | null;
  multiple?: boolean;
  selectSubtrees?: boolean;
  selectedValues?: string[];
  coveredByIds?: string[];
  selectionDisabled?: boolean;
  defaultExpanded?: boolean;
  expandedById: Record<string, boolean>;
  searchCollapsed: Set<number>;
  query: string;
  onToggle: (id: number) => void;
  onSelect: (value: string) => void;
}

/** Renders nested lists so each connector belongs to its real parent and visible sibling set. */
function DepartmentTreeBranch({
  nodes,
  depth,
  stickyTopOffset,
  stickyDepthLimit = 3,
  selectedValue,
  multiple = false,
  selectSubtrees = false,
  selectedValues = [],
  coveredByIds = [],
  selectionDisabled = false,
  defaultExpanded = false,
  expandedById,
  searchCollapsed,
  query,
  onToggle,
  onSelect,
}: DepartmentTreeBranchProps) {
  return (
    <ul
      className={depth === 0 ? 'department-select-tree-root' : 'department-select-tree-children'}
      role={depth === 0 ? 'tree' : 'group'}
    >
      {nodes.map((node) => {
        const expanded = query
          ? !searchCollapsed.has(node.id)
          : expandedById[String(node.id)] === undefined
          ? defaultExpanded
          : expandedById[String(node.id)] === true;
        const hasChildren = node.children.length > 0;
        const ancestorIds = node.item?.breadcrumbs.slice(0, -1).map(({ departmentId }) => String(departmentId)) ?? [];
        const inherited =
          multiple &&
          selectSubtrees &&
          ancestorIds.some((id) => selectedValues.includes(id) || coveredByIds.includes(id));
        const checked = multiple && (inherited || selectedValues.includes(String(node.id)));
        const descendantSelected =
          multiple &&
          selectSubtrees &&
          !checked &&
          selectedValues.some((id) => {
            /** Finds an explicitly selected node at any depth below the current branch. */
            const findDescendant = (children: DepartmentTreeNode[]): boolean =>
              children.some((child) => child.id === Number(id) || findDescendant(child.children));
            return findDescendant(node.children);
          });
        return (
          <li
            key={node.id}
            className={`department-select-tree-node${hasChildren ? ' department-select-tree-node-parent' : ''}`}
            role="treeitem"
            aria-expanded={hasChildren ? expanded : undefined}
            aria-disabled={!node.item && !hasChildren ? true : undefined}
            aria-selected={node.item && !multiple ? selectedValue === String(node.id) : undefined}
            data-testid={`department-select-node-${node.id}`}
            data-hierarchy-depth={depth}
          >
            <div
              className={`department-select-tree-row${hasChildren ? ' department-select-tree-parent' : ''}${
                hasChildren && depth >= stickyDepthLimit ? ' department-select-tree-parent-deep' : ''
              }${hasChildren && expanded ? ' department-select-tree-expanded' : ''}`}
              data-testid={`department-select-row-${node.id}`}
              style={{
                top: hasChildren && depth < stickyDepthLimit ? stickyTopOffset + depth * 36 : undefined,
                zIndex: 100 - depth,
              }}
            >
              {hasChildren && depth > 0 && <span className="department-select-tree-sticky-lead" aria-hidden="true" />}
              {hasChildren ? (
                <button
                  type="button"
                  className="department-select-tree-toggle"
                  aria-label={`${expanded ? 'Zbaliť' : 'Rozbaliť'} ${node.name}`}
                  aria-expanded={expanded}
                  onClick={() => onToggle(node.id)}
                >
                  {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                </button>
              ) : (
                <span className="department-select-tree-toggle" aria-hidden="true" />
              )}
              {node.item && multiple ? (
                <div
                  className="department-select-tree-choice department-select-tree-checkbox-row"
                  title={formatDepartmentPath(node.item.breadcrumbs)}
                >
                  <Checkbox
                    label={
                      <span>
                        {node.item.status ? '' : '[VYRADENÉ] '}
                        {highlightDepartmentName(node.name, query)}
                      </span>
                    }
                    aria-label={node.name}
                    checked={checked}
                    indeterminate={descendantSelected}
                    aria-checked={descendantSelected ? 'mixed' : checked}
                    disabled={inherited || selectionDisabled}
                    aria-readonly={inherited}
                    onChange={() => onSelect(String(node.id))}
                  />
                  <span className="department-select-tree-level">{node.levelLabel}</span>
                </div>
              ) : node.item ? (
                <button
                  type="button"
                  className="department-select-tree-choice"
                  aria-label={node.name}
                  title={formatDepartmentPath(node.item.breadcrumbs)}
                  data-selected={selectedValue === String(node.id) || undefined}
                  disabled={selectionDisabled}
                  onClick={() => onSelect(String(node.id))}
                >
                  <span>
                    {node.item.status ? '' : '[VYRADENÉ] '}
                    {highlightDepartmentName(node.name, query)}
                  </span>
                  <span className="department-select-tree-level">{node.levelLabel}</span>
                </button>
              ) : (
                <span className="department-select-tree-context">
                  <span>{highlightDepartmentName(node.name, query)}</span>
                  <span className="department-select-tree-level">{node.levelLabel}</span>
                </span>
              )}
            </div>
            {hasChildren && expanded && (
              <DepartmentTreeBranch
                nodes={node.children}
                depth={depth + 1}
                stickyTopOffset={stickyTopOffset}
                stickyDepthLimit={stickyDepthLimit}
                selectedValue={selectedValue}
                multiple={multiple}
                selectSubtrees={selectSubtrees}
                selectedValues={selectedValues}
                coveredByIds={coveredByIds}
                selectionDisabled={selectionDisabled}
                defaultExpanded={defaultExpanded}
                expandedById={expandedById}
                searchCollapsed={searchCollapsed}
                query={query}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Select input for departments.
 */
function ModalTreeDepartmentSelectStrategy({
  showDiscarded = true,
  groupByOrganization = false,
  organizationId,
  organizationIds = EMPTY_ORGANIZATION_IDS,
  permissionSlug,
  excludeDepartmentId,
  preloadedDepartments,
  followedOnly,
  userId,
  allowLegacy,
  value,
  defaultValue,
  onChange,
  onReady,
  filterData,
  autoSelectSingleResult = false,
  lazyLoad = false,
  loadingPlaceholder = 'Načítavanie ...',
  clearable,
  readOnly,
  disabled,
  rightSection,
  ...inputProps
}: DepartmentSelectProps) {
  const { fetchDepartments } = useDepartmentSelect({
    showDiscarded,
    groupByOrganization,
    organizationId,
    organizationIds,
    permissionSlug,
    excludeDepartmentId,
    preloadedDepartments,
    followedOnly,
    userId,
  });

  const departmentText = useDepartmentText();
  const scopeKey = JSON.stringify({
    showDiscarded,
    groupByOrganization,
    organizationId,
    organizationIds: organizationIds.map(String),
    permissionSlug,
    excludeDepartmentId,
  });
  const [opened, setOpened] = useState(false);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedById, setExpandedById] = useLocalStorage<Record<string, boolean>>(EXPANDED_STORAGE_KEY, {});
  const [autoConfirmPreference, setAutoConfirmPreference] = useLocalStorage<{ enabled: boolean }>(
    AUTO_CONFIRM_STORAGE_KEY,
    { enabled: false }
  );
  const [searchCollapsed, setSearchCollapsed] = useState<Set<number>>(new Set());
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [dataState, setDataState] = useState<{ scopeKey: string; rows: DepartmentSelectItem[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [requested, setRequested] = useState(!lazyLoad || Boolean(value ?? defaultValue));
  const [internalValue, setInternalValue] = useState<string | null>(defaultValue == null ? null : String(defaultValue));
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const data = dataState?.scopeKey === scopeKey ? dataState.rows : null;
  const selectedValue = value === undefined ? internalValue : value == null || value === '' ? null : String(value);
  const filteredData = useMemo(() => (filterData ? filterData(data ?? []) : data ?? []), [data, filterData]);
  const tree = useMemo(() => buildDepartmentTree(filteredData), [filteredData]);
  const query = removeAccents(search.trim()).toLocaleLowerCase('sk');
  const visibleTree = useMemo(
    () =>
      tree
        .map((group) => ({ ...group, roots: filterDepartmentTree(group.roots, query) }))
        .filter((group) => group.roots.length),
    [tree, query]
  );
  const parentIds = useMemo(() => {
    const ids: number[] = [];
    /** Collects each branch ID so the footer can update the complete tree. */
    const collect = (nodes: DepartmentTreeNode[]) =>
      nodes.forEach((node) => {
        if (node.children.length) {
          ids.push(node.id);
          collect(node.children);
        }
      });
    tree.forEach((group) => collect(group.roots));
    return ids;
  }, [tree]);

  useEffect(() => {
    if (lazyLoad && selectedValue !== null) setRequested(true);
  }, [lazyLoad, selectedValue]);

  useEffect(() => {
    if (!requested) return undefined;
    let active = true;
    setLoading(true);
    setFailed(false);
    setDataState(null);
    fetchDepartments()
      .then((departments) => {
        if (!active) return;
        setDataState({ scopeKey, rows: departments });
        onReadyRef.current?.(departments);
      })
      .catch((error) => {
        if (active) {
          panic(error);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchDepartments, requested, retry, scopeKey]);

  useEffect(() => {
    if (data && selectedValue === null) {
      const autoSelect = filteredData.find(
        (item) => (item as DepartmentSelectItem & { autoSelect?: boolean }).autoSelect
      );
      const onlyItem = autoSelectSingleResult && filteredData.length === 1 ? filteredData[0] : undefined;
      const nextValue = autoSelect ?? onlyItem;
      if (nextValue) {
        if (value === undefined) setInternalValue(nextValue.value);
        onChange?.(nextValue.value);
      }
    }
  }, [data, filteredData, selectedValue, autoSelectSingleResult, onChange, value]);

  /** Closes both the picker and its settings popover. */
  const close = () => {
    setSettingsOpened(false);
    setOpened(false);
  };
  /** Emits the same ID value as the former select and closes the picker. */
  const changeValue = (nextValue: string | null) => {
    if (disabled || readOnly) return;
    if (value === undefined) setInternalValue(nextValue);
    onChange?.(nextValue);
    close();
  };
  /** Opens the tree with the stored branch state and the current committed choice. */
  const open = () => {
    if (disabled || readOnly) return;
    setExpandedById(readExpandedPreference());
    setAutoConfirmPreference({ enabled: readAutoConfirmPreference() });
    setSettingsOpened(false);
    setSearch('');
    setSearchCollapsed(new Set());
    setPendingValue(selectedValue);
    setRequested(true);
    setOpened(true);
  };
  /** Toggles one branch without changing its siblings. */
  const toggle = (id: number) => {
    const expanded = query ? !searchCollapsed.has(id) : expandedById[String(id)] === true;
    setExpandedById((previous) => ({ ...previous, [id]: !expanded }));
    if (query)
      setSearchCollapsed((previous) => {
        const next = new Set(previous);
        if (expanded) next.add(id);
        else next.delete(id);
        return next;
      });
  };
  /** Applies a bulk branch choice to every known parent. */
  const setAllExpanded = (expanded: boolean) => {
    setExpandedById((previous) => {
      const next = { ...previous };
      parentIds.forEach((id) => {
        next[id] = expanded;
      });
      return next;
    });
    setSearchCollapsed(expanded ? new Set() : new Set(parentIds));
  };
  const selectedItem = filteredData.find((item) => item.value === selectedValue);
  const selectedLevelLabel = selectedItem?.breadcrumbs[selectedItem.breadcrumbs.length - 1]?.levelLabel;
  const pendingItem = filteredData.find((item) => item.value === pendingValue);
  const hasClearAction = Boolean(clearable && selectedValue && !disabled && !readOnly);

  return (
    <>
      <input type="hidden" name={inputProps.name} value={selectedValue ?? ''} readOnly />
      <TextInput
        {...(inputProps as TextInputProps)}
        name={undefined}
        className={`department-select-input${disabled || readOnly ? ' department-select-input-locked' : ''}${
          inputProps.className ? ` ${inputProps.className}` : ''
        }`}
        value={selectedItem?.departmentName ?? ''}
        placeholder={loading && !lazyLoad ? loadingPlaceholder : inputProps.placeholder}
        readOnly
        disabled={disabled}
        icon={
          selectedItem && selectedItem.breadcrumbs.length > 1 ? (
            <button
              type="button"
              className="department-select-input-path"
              aria-label={formatDepartmentPath(selectedItem.breadcrumbs)}
              disabled={disabled || readOnly}
              onClick={open}
            >
              <DepartmentParentReference breadcrumbs={selectedItem.breadcrumbs} showFullPath />
            </button>
          ) : (
            inputProps.icon
          )
        }
        rightSectionWidth={
          inputProps.rightSectionWidth ??
          (selectedLevelLabel ? `min(50%, calc(80px + ${selectedLevelLabel.length}ch))` : undefined)
        }
        onClick={(event) => {
          inputProps.onClick?.(event);
          open();
        }}
        onKeyDown={(event) => {
          inputProps.onKeyDown?.(event);
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            open();
          }
        }}
        aria-haspopup="dialog"
        rightSectionProps={{
          ...inputProps.rightSectionProps,
          onClick: (event: React.MouseEvent<HTMLDivElement>) => {
            inputProps.rightSectionProps?.onClick?.(event);
            if (!(event.target as Element).closest('.department-select-clear')) open();
          },
        }}
        rightSection={
          <span className="department-select-input-right">
            {selectedLevelLabel && (
              <Badge size="xs" className="department-select-input-level">
                {selectedLevelLabel}
              </Badge>
            )}
            {hasClearAction ? (
              <button
                type="button"
                className="department-select-clear"
                aria-label="Vymazať výber"
                onClick={() => changeValue(null)}
              >
                <IconX size={16} />
              </button>
            ) : (
              rightSection ?? <IconChevronDown size={16} aria-hidden="true" />
            )}
          </span>
        }
      />
      <Modal.Root
        opened={opened}
        onClose={close}
        size="xl"
        centered
        classNames={{ content: 'department-select-modal-content', body: 'department-select-modal-body' }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header className="department-select-modal-header">
            <div className="department-select-modal-heading">
              <Modal.Title>{departmentText('Vybrať položku úrovne', 'Vybrať stredisko')}</Modal.Title>
              <Popover
                opened={settingsOpened}
                onChange={setSettingsOpened}
                position="bottom-start"
                width={300}
                shadow="md"
                withinPortal={false}
              >
                <Popover.Target>
                  <ActionIcon
                    variant="subtle-gray"
                    size="md"
                    aria-label="Nastavenia výberu"
                    onClick={() => setSettingsOpened((current) => !current)}
                  >
                    <IconSettings size={18} aria-hidden="true" />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') event.stopPropagation();
                  }}
                >
                  <h3 className="department-select-settings-title">Nastavenia výberu</h3>
                  <Checkbox
                    size="sm"
                    label="Potvrdzovať výber automaticky"
                    checked={autoConfirmPreference.enabled}
                    onChange={(event) => setAutoConfirmPreference({ enabled: event.currentTarget.checked })}
                  />
                </Popover.Dropdown>
              </Popover>
            </div>
            <Modal.CloseButton aria-label="Zavrieť výber" autoFocus data-autofocus />
          </Modal.Header>
          <Modal.Body>
            <div className="department-select-modal-search pt-1">
              <TextInput
                value={search}
                onChange={(event) => {
                  setSearch(event.currentTarget.value);
                  setSearchCollapsed(new Set());
                }}
                placeholder={departmentText('Hľadať položky úrovne', 'Hľadať strediská')}
                aria-label={departmentText('Hľadať položky úrovne', 'Hľadať strediská')}
                icon={<IconSearch size={16} aria-hidden="true" />}
              />
            </div>
            <ScrollArea.Autosize className="department-select-modal-tree" type="auto" offsetScrollbars>
              {loading && <p>Načítavanie ...</p>}
              {failed && (
                <button type="button" onClick={() => setRetry((count) => count + 1)}>
                  Načítanie zlyhalo. Skúsiť znova
                </button>
              )}
              {!loading && !failed && !visibleTree.length && <p>Žiadne výsledky</p>}
              {!loading &&
                !failed &&
                visibleTree.map((group) => (
                  <div key={group.id} className="department-select-tree-group">
                    {groupByOrganization && <div className="department-select-tree-group-name">{group.name}</div>}
                    <DepartmentTreeBranch
                      nodes={group.roots}
                      depth={0}
                      stickyTopOffset={groupByOrganization ? 36 : 0}
                      selectedValue={pendingValue}
                      selectionDisabled={disabled || readOnly}
                      expandedById={expandedById}
                      searchCollapsed={searchCollapsed}
                      query={query}
                      onToggle={toggle}
                      onSelect={(nextValue) => {
                        if (autoConfirmPreference.enabled) changeValue(nextValue);
                        else setPendingValue(nextValue);
                      }}
                    />
                  </div>
                ))}
            </ScrollArea.Autosize>
            <footer className="department-select-modal-footer" data-testid="department-select-modal-footer">
              <div className="department-select-modal-bulk">
                <Button
                  size="sm"
                  variant="subtle-gray"
                  leftIcon={<IconChevronsDown size={16} />}
                  onClick={() => setAllExpanded(true)}
                >
                  Rozbaliť všetko
                </Button>
                <Button
                  size="sm"
                  variant="subtle-gray"
                  leftIcon={<IconChevronsUp size={16} />}
                  onClick={() => setAllExpanded(false)}
                >
                  Zbaliť všetko
                </Button>
              </div>
              <div className="department-select-modal-actions">
                <Button size="md" variant="subtle-gray" onClick={close}>
                  Zrušiť
                </Button>
                {!autoConfirmPreference.enabled && (
                  <Button
                    size="md"
                    disabled={!pendingItem || disabled || readOnly}
                    onClick={() => pendingItem && changeValue(pendingItem.value)}
                  >
                    Potvrdiť výber
                  </Button>
                )}
              </div>
            </footer>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}

const departmentSelectStrategies: Record<DepartmentSelectStrategyName, React.ComponentType<DepartmentSelectProps>> = {
  legacy: LegacyDepartmentSelectStrategy,
  tree: ModalTreeDepartmentSelectStrategy,
};

/** Holds the field in place without loading options or selecting a value during strategy resolution. */
function PendingDepartmentSelect({
  label,
  name,
  value,
  defaultValue,
  placeholder,
  size,
  error,
  required,
  'aria-label': ariaLabel,
}: DepartmentSelectProps) {
  return (
    <>
      <TextInput
        label={label}
        aria-label={ariaLabel}
        placeholder={placeholder ?? 'Načítavanie ...'}
        size={size}
        error={error}
        required={required}
        disabled
      />
      {name && <input type="hidden" name={name} value={value ?? defaultValue ?? ''} readOnly />}
    </>
  );
}

/** Uses the same hierarchy rows for independent and subtree multi-selection. */
function TreeDepartmentMultiSelect({
  multiple,
  selectSubtrees = false,
  pruneCoveredIds = false,
  inline = false,
  popover = false,
  confirmSelection: confirmSelectionProp,
  excludeSubtrees = false,
  excludedIds = [],
  coveredByIds = [],
  showSelectionCount = false,
  withinPortal = true,
  loading: externalLoading = false,
  loadError = false,
  onRetry,
  onPickerClose,
  emptyText = 'Žiadne výsledky',
  errorText = 'Načítanie zlyhalo.',
  showDiscarded = true,
  groupByOrganization = false,
  organizationId,
  organizationIds = EMPTY_ORGANIZATION_IDS,
  permissionSlug,
  excludeDepartmentId,
  preloadedDepartments,
  followedOnly = false,
  userId,
  value,
  defaultValue,
  onChange,
  onReady,
  filterData,
  lazyLoad = false,
  loadingPlaceholder = 'Načítavanie ...',
  label,
  placeholder,
  size,
  name,
  disabled,
  readOnly,
  error,
  required,
  withAsterisk,
  clearable,
  compact,
  ...inputProps
}: DepartmentMultiSelectProps) {
  const confirmSelection = confirmSelectionProp ?? (!inline && !popover);
  const departmentText = useDepartmentText();
  const { fetchDepartments } = useDepartmentSelect({
    showDiscarded,
    groupByOrganization,
    organizationId,
    organizationIds,
    permissionSlug,
    excludeDepartmentId,
    preloadedDepartments,
    followedOnly,
    userId,
  });
  const [data, setData] = useState<DepartmentSelectItem[] | null>(null);
  const [loading, setLoading] = useState(!lazyLoad);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [requested, setRequested] = useState(!lazyLoad || Boolean(value?.length || defaultValue?.length));
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});
  const [searchCollapsed, setSearchCollapsed] = useState<Set<number>>(new Set());
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue?.map(String) ?? []);
  const [pendingValue, setPendingValue] = useState<string[]>([]);
  const selected = value === undefined ? internalValue : value.map(String);
  const activeSelected = confirmSelection && opened ? pendingValue : selected;
  const excluded = useMemo(() => new Set(excludedIds.map(String)), [excludedIds.join(',')]);
  const covered = useMemo(() => coveredByIds.map(String), [coveredByIds.join(',')]);
  const options = useMemo(() => {
    const filtered: DepartmentSelectItem[] = filterData ? filterData(data ?? []) : data ?? [];
    return filtered.filter((item) => {
      if (excluded.has(String(item.departmentId))) return false;
      return !excludeSubtrees || !item.breadcrumbs.some(({ departmentId }) => excluded.has(String(departmentId)));
    });
  }, [data, filterData, excluded, excludeSubtrees]);
  const tree = useMemo(() => buildDepartmentTree(options), [options]);
  const query = removeAccents(search.trim()).toLocaleLowerCase('sk');
  const visibleTree = useMemo(
    () =>
      tree
        .map((group) => ({ ...group, roots: filterDepartmentTree(group.roots, query, true) }))
        .filter((group) => group.roots.length),
    [tree, query]
  );
  const accessibleDescendants = options.filter(
    (item) =>
      !selected.includes(item.value) &&
      item.breadcrumbs.slice(0, -1).some(({ departmentId }) => selected.includes(String(departmentId)))
  ).length;
  const summary = showSelectionCount
    ? selected.length
      ? `${departmentText('Vybrané položky úrovne:', 'Vybrané strediská:')} ${selected.length}+${accessibleDescendants}`
      : null
    : selected.length > 1
    ? `Vybrané: ${selected.length}`
    : options.find((item) => item.value === selected[0])?.label ?? selected[0] ?? null;

  useEffect(() => {
    if (!requested) return undefined;
    let active = true;
    setLoading(true);
    setFailed(false);
    fetchDepartments()
      .then((rows) => {
        if (!active) return;
        setData(rows);
        onReady?.(rows);
      })
      .catch((cause) => {
        if (active) {
          panic(cause);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchDepartments, requested, retry]);

  /** Emits exactly the explicit IDs owned by the caller. */
  const commit = (ids: string[]) => {
    if (disabled || readOnly) return;
    if (value === undefined) setInternalValue(ids);
    onChange?.(ids);
  };
  /** Applies a checkbox choice and the configured explicit-ID pruning rule. */
  const choose = (id: string) => {
    if (disabled || readOnly) return;
    const existing = activeSelected.includes(id);
    let next = existing ? activeSelected.filter((candidate) => candidate !== id) : [...activeSelected, id];
    if (!existing && selectSubtrees && pruneCoveredIds) {
      next = next.filter((candidate) => {
        if (candidate === id) return true;
        const item = options.find((row) => row.value === candidate);
        return !item?.breadcrumbs.slice(0, -1).some(({ departmentId }) => String(departmentId) === id);
      });
    }
    if (confirmSelection && opened) setPendingValue(next);
    else commit(next);
  };
  /** Opens a new editing session from the controlled or internal value. */
  const open = () => {
    if (disabled || readOnly) return;
    setSearch('');
    setSearchCollapsed(new Set());
    setPendingValue(selected);
    setRequested(true);
    setOpened(true);
  };
  /** Discards any staged choice. */
  const close = () => {
    setOpened(false);
    onPickerClose?.();
  };
  /** Expands or collapses one branch without changing the selected IDs. */
  const toggle = (id: number) => {
    const expanded = query ? !searchCollapsed.has(id) : expandedById[String(id)] !== false;
    setExpandedById((previous) => ({ ...previous, [id]: !expanded }));
    if (query)
      setSearchCollapsed((previous) => {
        const next = new Set(previous);
        if (expanded) next.add(id);
        else next.delete(id);
        return next;
      });
  };
  const picker = (
    <>
      <TextInput
        value={search}
        onChange={(event) => {
          setSearch(event.currentTarget.value);
          setSearchCollapsed(new Set());
        }}
        placeholder={departmentText('Hľadať položky úrovne', 'Hľadať strediská')}
        aria-label={departmentText('Hľadať položky úrovne', 'Hľadať strediská')}
        icon={<IconSearch size={16} aria-hidden="true" />}
      />
      <Box className="department-select-multi-tree">
        {(loading || externalLoading) && <p>{loadingPlaceholder}</p>}
        {(failed || loadError) && (
          <div>
            <p>{errorText}</p>
            <button type="button" onClick={() => (onRetry ? onRetry() : setRetry((count) => count + 1))}>
              Skúsiť znova
            </button>
          </div>
        )}
        {!loading && !externalLoading && !failed && !loadError && !visibleTree.length && <p>{emptyText}</p>}
        {!loading &&
          !externalLoading &&
          !failed &&
          !loadError &&
          visibleTree.map((group) => (
            <div key={group.id} className="department-select-tree-group">
              {groupByOrganization && <div className="department-select-tree-group-name">{group.name}</div>}
              <DepartmentTreeBranch
                nodes={group.roots}
                depth={0}
                stickyTopOffset={groupByOrganization ? 36 : 0}
                stickyDepthLimit={popover ? 2 : 3}
                selectedValue={null}
                multiple
                selectSubtrees={selectSubtrees}
                selectedValues={activeSelected}
                coveredByIds={covered}
                selectionDisabled={disabled || readOnly}
                defaultExpanded
                expandedById={expandedById}
                searchCollapsed={searchCollapsed}
                query={query}
                onToggle={toggle}
                onSelect={choose}
              />
            </div>
          ))}
      </Box>
      {!inline && (
        <div className="department-select-multi-actions">
          <Button
            variant="subtle"
            size="sm"
            disabled={disabled || readOnly || !activeSelected.length}
            onClick={() => (confirmSelection ? setPendingValue([]) : commit([]))}
          >
            Zrušiť výber
          </Button>
          {confirmSelection && (
            <div>
              <Button variant="subtle" onClick={close}>
                Zrušiť
              </Button>
              <Button
                disabled={disabled || readOnly}
                onClick={() => {
                  commit(pendingValue);
                  close();
                }}
              >
                Potvrdiť výber
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (inline) return <div className="department-select-inline">{picker}</div>;
  if (popover)
    return (
      <Popover
        opened={opened}
        onChange={(nextOpened) => (nextOpened ? setOpened(true) : close())}
        position="bottom-start"
        withinPortal={withinPortal}
        trapFocus
        returnFocus
        width="min(720px, calc(100vw - 72px))"
      >
        <Popover.Target>
          <Input
            component="button"
            type="button"
            pointer
            disabled={disabled || readOnly}
            size={size ?? 'sm'}
            w="100%"
            aria-label={inputProps['aria-label'] ?? departmentText('Filtrovať položky úrovne', 'Filtrovať strediská')}
            aria-expanded={opened}
            onClick={() => (opened ? close() : open())}
            rightSection={<IconChevronDown size={16} />}
            styles={{ wrapper: { alignSelf: 'flex-start' }, rightSection: { pointerEvents: 'none' } }}
          >
            {summary ?? (
              <Input.Placeholder>
                {placeholder ?? departmentText('Všetky položky úrovne', 'Všetky strediská')}
              </Input.Placeholder>
            )}
          </Input>
        </Popover.Target>
        <Popover.Dropdown
          className="ag-custom-component-popup department-select-popover"
          aria-label={departmentText('Výber položiek úrovne', 'Výber stredísk')}
          onKeyDown={(event) => {
            if (event.key === 'Escape') event.stopPropagation();
          }}
        >
          {picker}
        </Popover.Dropdown>
      </Popover>
    );
  return (
    <>
      {name && <input type="hidden" name={name} value={selected.join(',')} readOnly />}
      <TextInput
        {...(inputProps as TextInputProps)}
        classNames={{
          ...inputProps.classNames,
          input: `department-select-input${disabled || readOnly ? ' department-select-input-locked' : ''}${
            inputProps.classNames?.input ? ` ${inputProps.classNames.input}` : ''
          }`,
        }}
        label={label}
        placeholder={placeholder}
        size={size}
        value={summary ?? ''}
        readOnly
        disabled={disabled}
        error={error}
        required={required}
        withAsterisk={withAsterisk}
        onClick={() => (opened ? close() : open())}
        onKeyDown={(event) => {
          inputProps.onKeyDown?.(event);
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            open();
          }
        }}
        aria-haspopup="dialog"
        rightSection={<IconChevronDown size={16} />}
      />
      <Modal
        opened={opened}
        onClose={close}
        title={departmentText('Vybrať položky úrovne', 'Vybrať strediská')}
        size="xl"
        centered
        classNames={{ content: 'department-select-multi-modal-content', body: 'department-select-multi-modal-body' }}
      >
        {picker}
      </Modal>
    </>
  );
}

/** The single selection module shared by forms, pickers, and table filters. */
export function DepartmentSelect(props: DepartmentSelectProps): React.ReactElement;
export function DepartmentSelect(props: DepartmentMultiSelectProps): React.ReactElement;
/** Selects the tree or legacy single presentation from the discriminated props. */
export function DepartmentSelect(props: DepartmentSelectProps | DepartmentMultiSelectProps): React.ReactElement {
  const { name, pending } = useDepartmentSelectStrategy();
  if (props.multiple) {
    if (name === 'legacy' && props.allowLegacy !== false && !props.inline && !props.popover) {
      return <LegacyDepartmentMultiSelect {...props} />;
    }
    return <TreeDepartmentMultiSelect {...props} />;
  }
  if (pending && props.allowLegacy !== false) return <PendingDepartmentSelect {...props} />;
  const SelectedStrategy =
    props.allowLegacy === false ? ModalTreeDepartmentSelectStrategy : departmentSelectStrategies[name];
  return <SelectedStrategy {...props} />;
}
